import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { isUsableContactName } from '../../../../utils/phoneUtils';
import { ChatConversation } from '../../hooks/useChat';
import { ChatAvatar } from '../chat-window/ChatAvatar';
import { useSyncAvatar } from '../../hooks/useSyncAvatar';

interface ContactPanelHeaderProps {
    conversation: ChatConversation;
    contactName?: string | null;
    /** Já formatado (@username); só Instagram */
    instagramHandle?: string | null;
    onCollapse: () => void;
    onEditContact?: () => void;
}

export const ContactPanelHeader: React.FC<ContactPanelHeaderProps> = ({
    conversation,
    contactName,
    instagramHandle,
    onCollapse,
    onEditContact,
}) => {
    const isInstagram = conversation.platform === 'instagram';
    const phoneNumber = conversation.id.split('@')[0];
    const rawTitle = (contactName || conversation.name || '').trim();
    // Sem @ no título quando o nome ainda é placeholder (CRM/mapper).
    const title = isInstagram && instagramHandle && !isUsableContactName(rawTitle)
        ? instagramHandle
        : (contactName || conversation.name);
    const subtitle = isInstagram
        ? (instagramHandle && title !== instagramHandle ? instagramHandle : '')
        : `+${phoneNumber}`;

    const { syncAvatar, isSyncing } = useSyncAvatar();
    const [localAvatar, setLocalAvatar] = React.useState(conversation.avatar);

    React.useEffect(() => {
        setLocalAvatar(conversation.avatar);
    }, [conversation.avatar]);

    const handleSyncAvatar = async () => {
        if (isInstagram || !conversation.contactId) return;
        const newAvatarUrl = await syncAvatar(conversation.contactId, conversation.instanceName);
        if (newAvatarUrl) {
            setLocalAvatar(newAvatarUrl);
        }
    };

    return (
        <header className="bg-card">
            {/* Altura h-16 = ChatHeader — alinha o X com os ícones da conversa */}
            <div className="flex h-16 items-center gap-1 pr-4">
                <button
                    type="button"
                    onClick={onCollapse}
                    className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-primary/10 hover:text-primary pointer-fine:size-9"
                    title="Fechar"
                    aria-label="Fechar painel do contato"
                >
                    <AppIcon name="close" className="size-5 stroke-[1.5]" aria-hidden="true" />
                </button>
                <h2 className="chat-label">Dados do contato</h2>
                {onEditContact && (
                    <button
                        type="button"
                        onClick={onEditContact}
                        className="ml-auto inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-muted transition-colors hover:bg-primary/10 hover:text-primary pointer-fine:size-9"
                        title="Editar contato"
                        aria-label="Editar contato"
                    >
                        <AppIcon name="edit" className="size-4 stroke-[1.5]" aria-hidden />
                    </button>
                )}
            </div>

            <div className="flex min-w-0 items-center gap-3 px-4 pb-3">
                <ChatAvatar
                    name={conversation.name}
                    avatar={localAvatar}
                    size="lg"
                    customBadgeIcon={isInstagram ? 'instagram' : 'whatsapp'}
                    onClick={isInstagram ? undefined : handleSyncAvatar}
                    isSyncing={isInstagram ? false : Boolean(isSyncing[conversation.contactId || ''])}
                />
                <div className="flex min-w-0 flex-1 flex-col justify-center">
                    <p className="chat-contact-name truncate leading-tight" title={title}>{title}</p>
                    {subtitle ? (
                        <p className="chat-mono-phone mt-0.5 truncate tabular-nums">{subtitle}</p>
                    ) : null}
                </div>
            </div>
        </header>
    );
};
